import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { MEETINGS_AGENT_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of the Meetings channel arm — `symphonyMeetingsAgent`.
 *
 * Variant (`true`): the Channels tab carries the Meetings section and the hero diagram carries its
 * node. Control (`false`): neither exists, and the `channels/meetings` deeplink lands on a tab with no
 * such section — the same place an unknown slug lands today.
 *
 * It FAILS CLOSED to CONTROL, like the sibling gates: a gate that has not answered (or errored) shows
 * what already works rather than offering to connect a bot to somebody's calls.
 *
 * **This gate hides a section; it does not protect anything.** The three `/api/meetings/*` proxies run
 * the same conduction server-side (`lib/meetings-agent-flag-server.ts`), so forcing this On in dev
 * tools reveals the section and still gets 501s from the routes behind it unless the account is
 * genuinely in the release. That is deliberate: a dev override is a rendering convenience, never an
 * entitlement.
 *
 * A dev-tools **Feature toggles** override wins over the conducted value (`toggleOverride` is consulted
 * first), and that override is inert without a dev-tools grant, so a real user always gets the
 * conducted value.
 */

/** Matches the sibling gates' bound — a gate that has not answered in time IS control. */
const GATE_TIMEOUT_MS = 1500;

let cached: Promise<boolean> | null = null;

export function meetingsAgentEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(MEETINGS_AGENT_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/meetings-agent`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) return false;
      return ((await res.json()) as boolean) === true;
    } catch {
      // Includes the abort: a gate that has not answered in time IS control.
      return false;
    }
  })();
  return cached;
}

/**
 * The arm, for rendering. Starts false — no Meetings section — so a user outside the release never
 * sees it flash in and out.
 */
export function useMeetingsAgentEnabled(): boolean {
  return useMeetingsAgentGate().enabled;
}

/**
 * The arm plus whether it has answered yet. `enabled` is the same fail-closed value as above; `resolved`
 * lets a caller hold a decision that the answer could reverse — a `/chats?channel=CALENDAR_MEETINGS`
 * link must not auto-open All's first row in the moment before the gate says the tab exists.
 */
export function useMeetingsAgentGate(): { enabled: boolean; resolved: boolean } {
  const [gate, setGate] = useState({ enabled: false, resolved: false });
  useEffect(() => {
    let live = true;
    void meetingsAgentEnabled().then((on) => {
      if (live) setGate({ enabled: on, resolved: true });
    });
    return () => {
      live = false;
    };
  }, []);
  return gate;
}
